import { useState, useCallback, useEffect } from 'react';
import { cva } from 'class-variance-authority';
import clsx from 'clsx';
import { motion } from 'motion/react';
import { Flex, Text, Checkbox, PageCardGrid, ExpandableSearch, Dropdown, Badge, Segmented, EditableText, Popover, space, iconWeight, Icon, IconButton, useReducedMotion, Table } from '@nula/core-ui';
import styles from './library.module.css';
import { DocumentsIllustration, CollectionsIllustration, TrashIllustration } from '@/components/empty-illustrations';
export type AccessFilter = 'all' | 'shared';

// ── CVA ──────────────────────────────────────────────────────────────────────

const root = cva(styles.root);

const heroContent = cva(styles.heroContent, {
  variants: {
    constrained: { true: styles.heroContentConstrained },
    colored: { true: styles.heroContentColorPadded, false: styles.heroContentPadded },
  },
  defaultVariants: { constrained: false, colored: false },
});

const heroIconWrap = cva(styles.heroIconWrap, {
  variants: {
    colored: { true: styles.heroIconWrapColored, false: styles.heroIconWrapDefault },
  },
  defaultVariants: { colored: false },
});

const contentArea = cva(styles.contentArea, {
  variants: {
    constrained: { true: styles.contentAreaConstrained },
  },
  defaultVariants: { constrained: false },
});

// ── Types ────────────────────────────────────────────────────────────────────

interface LibraryProps<T> {
  /** Page title */
  title: string;
  /** Subtitle below title */
  subtitle?: string;
  /** Data items to display */
  items: T[];
  /** How to render each item card */
  renderCard: (item: T, index: number, viewMode?: 'grid' | 'list', checkboxNode?: React.ReactNode) => React.ReactNode;
  /** Key extractor — defaults to (item as any).id */
  keyExtractor?: (item: T) => string;
  /** Access ownership filter (All / Mine / Shared) */
  accessFilter?: AccessFilter;
  /** Called when access filter changes */
  onAccessFilterChange?: (filter: AccessFilter) => void;
  /** Type filter options shown in the ⋯ dropdown (e.g. All / Documents / Canvases) */
  typeOptions?: Array<{ key: string; label: string; icon?: React.ReactNode }>;
  /** Active type filter key */
  activeType?: string;
  /** Called when type filter changes */
  onTypeChange?: (type: string) => void;
  /** Search input placeholder */
  searchPlaceholder?: string;
  /** Called on search query change */
  onSearch?: (query: string) => void;
  /** Illustration variant for empty state */
  emptyIllustration?: 'documents' | 'collections' | 'trash';
  /** Title for empty state */
  emptyTitle?: string;
  /** Description for empty state */
  emptyMessage?: string;
  /** Extra actions rendered in the header (e.g. "New Document" button) */
  actions?: React.ReactNode;
  /** Icon buttons rendered in the header toolbar after search, before tag filter */
  toolbarExtras?: React.ReactNode;
  /** Pagination controls rendered below the grid */
  pagination?: React.ReactNode;
  /** Hero banner background color (default: transparent / page background) */
  heroColor?: string;
  /** Icon shown in the hero banner */
  heroIcon?: string;
  /** Item count shown in hero (e.g. "12 Documents") */
  heroCount?: string;
  /** Enable selection mode */
  selectable?: boolean;
  /** Currently selected item IDs */
  selectedIds?: Set<string>;
  /** Toggle selection for an item */
  onToggleSelect?: (id: string) => void;
  /** Called by Ctrl+A — receives all item IDs */
  onSelectAll?: (ids: string[]) => void;
  /** Called by Escape when selection is active */
  onClearSelection?: () => void;
  /** Floating bar rendered at bottom when items are selected */
  selectionActions?: React.ReactNode;
  /** If provided, title becomes editable on click */
  onRenameTitle?: (newTitle: string) => void;
  /** Current view mode */
  viewMode?: 'grid' | 'list';
  /** Called when view mode changes */
  onViewChange?: (mode: 'grid' | 'list') => void;
  /**
   * Grid layout selector. When set to 'pageGrid' the library uses the
   * uniform PageCardGrid (portrait 3:4 cards). When unset, the default
   * CSS grid is used.
   */
  cardLayout?: 'pageGrid';
  /** Minimum column width for CSS grid (default 280) */
  gridMinWidth?: number;
  /** Sort options shown in dropdown */
  sortOptions?: Array<{ key: string; label: string; icon?: React.ReactNode }>;
  /** Currently active sort key */
  activeSort?: string;
  /** Called when sort changes */
  onSortChange?: (key: string) => void;
  /** All available tags for filtering */
  tags?: string[];
  /** Currently selected tag filters */
  selectedTags?: string[];
  /** Called when tag selection changes */
  onTagsChange?: (tags: string[]) => void;
  /** Column definitions for table view — when provided and viewMode === 'list', renders a Table instead of card list */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  tableColumns?: any[];
  /** Row click handler for table view */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onTableRow?: (record: any, index: number) => React.HTMLAttributes<HTMLTableRowElement>;
  /** Row selection config for table view — enables checkboxes and drives the floating selection bar */
  tableRowSelection?: { selectedRowKeys: string[]; onChange: (keys: string[]) => void };
}

// ── Tag Filter Dropdown ──────────────────────────────────────────────────────

function TagFilterDropdown({
  tags,
  selectedTags,
  onTagsChange,
}: {
  tags: string[];
  selectedTags: string[];
  onTagsChange: (tags: string[]) => void;
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  const filtered = search.trim()
    ? tags.filter((t) => t.toLowerCase().includes(search.toLowerCase()))
    : tags;

  const toggle = useCallback((tag: string) => {
    if (selectedTags.includes(tag)) {
      onTagsChange(selectedTags.filter((t) => t !== tag));
    } else {
      onTagsChange([...selectedTags, tag]);
    }
  }, [selectedTags, onTagsChange]);

  const panelContent = (
    <Flex vertical>
      <Flex
        align="center"
        gap="xs"
        className={styles.tagPanelSearch}
      >
        <Icon name="search" size={14} weight={iconWeight.light} className={styles.tagSearchIcon} />
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Filter tags..."
          className={styles.tagSearchInput}
        />
      </Flex>
      <Flex vertical className={styles.tagList}>
        {filtered.map((tag) => (
          <Flex
            key={tag}
            align="center"
            gap="sm"
            role="option"
            aria-selected={selectedTags.includes(tag)}
            tabIndex={0}
            onClick={() => toggle(tag)}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggle(tag); } }}
            className={clsx(
              styles.tagRow,
              selectedTags.includes(tag) ? styles.tagRowSelected : styles.tagRowDefault,
            )}
          >
            <Checkbox checked={selectedTags.includes(tag)} onChange={() => toggle(tag)} />
            <Text size="sm">{tag}</Text>
          </Flex>
        ))}
      </Flex>
      {selectedTags.length > 0 && (
        <Flex
          align="center"
          justify="center"
          role="button"
          tabIndex={0}
          onClick={() => onTagsChange([])}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onTagsChange([]); } }}
          className={styles.tagClearAll}
        >
          Clear all
        </Flex>
      )}
    </Flex>
  );

  return (
    <Flex className={styles.tagBadgeWrap}>
      <Popover
        content={panelContent}
        trigger="click"
        placement="bottom-end"
        arrow={false}
        open={open}
        onOpenChange={setOpen}
      >
        <IconButton
          size="lg"
          aria-label="Filter by tags"
          className={styles.tagFilterBtn}
        >
          <Icon name="label" size={18} weight={iconWeight.light} />
        </IconButton>
      </Popover>
      {selectedTags.length > 0 && (
        <Badge
          count={selectedTags.length}
          className={styles.tagBadgeCount}
        />
      )}
    </Flex>
  );
}

// ── Component ────────────────────────────────────────────────────────────────

export function Library<T>({
  title,
  subtitle,
  items,
  renderCard,
  keyExtractor = (item) => (item as Record<string, unknown>).id as string,
  accessFilter,
  onAccessFilterChange,
  typeOptions,
  activeType,
  onTypeChange,
  searchPlaceholder,
  onSearch,
  emptyIllustration,
  emptyTitle,
  emptyMessage,
  actions,
  toolbarExtras,
  pagination,
  heroColor,
  heroIcon,
  heroCount,
  selectable: _selectable = false,
  selectedIds,
  onToggleSelect,
  onSelectAll,
  onClearSelection,
  selectionActions,
  onRenameTitle,
  viewMode,
  onViewChange,
  cardLayout,
  gridMinWidth,
  sortOptions,
  activeSort,
  onSortChange,
  tags,
  selectedTags,
  onTagsChange,
  tableColumns,
  onTableRow,
  tableRowSelection,
}: LibraryProps<T>) {
  const shouldReduce = useReducedMotion();
  const [searchQuery, setSearchQuery] = useState('');
  const [editing, setEditing] = useState(false);
  const hasHeroColor = !!heroColor;

  useEffect(() => {
    if (!onSelectAll && !onClearSelection) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'a' && (e.ctrlKey || e.metaKey)) {
        if (!onSelectAll) return;
        e.preventDefault();
        onSelectAll(items.map((item) => keyExtractor(item)));
      } else if (e.key === 'Escape') {
        if (!onClearSelection || !selectedIds?.size) return;
        onClearSelection();
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onSelectAll, onClearSelection, items, keyExtractor, selectedIds]);
  // The pageGrid layout fills the viewport; the default CSS grid stays
  // constrained for a calmer reading width.
  const wideLayout = cardLayout === 'pageGrid';

  const handleSearch = (value: string) => {
    setSearchQuery(value);
    onSearch?.(value);
  };

  // Shared renderer for the pageGrid / legacy grid branches — the selection
  // overlay + dimming wrapper is identical across both. The list-view branch
  // uses a different structure and does not use this helper.
  const renderSelectableGridItem = (item: T, index: number) => {
    const itemId = keyExtractor(item);
    const isSelected = selectedIds?.has(itemId) ?? false;
    const hasSelection = !!onToggleSelect;
    const isSelecting = hasSelection && (selectedIds?.size ?? 0) > 0;

    const checkboxNode = hasSelection ? (
      <Flex
        align="center"
        justify="center"
        className={styles.cardCheckbox}
        onClick={(e) => e.stopPropagation()}
      >
        <Checkbox
          checked={isSelected}
          onChange={() => onToggleSelect(itemId)}
          round
        />
      </Flex>
    ) : undefined;

    return (
      <Flex
        key={itemId}
        vertical
        className={clsx(
          styles.selectableItem,
          isSelecting && !isSelected && styles.selectableItemDimmed,
        )}
      >
        {renderCard(item, index, viewMode ?? 'grid', checkboxNode)}
        {/* Transparent click-capture overlay — only active in selection mode */}
        {isSelecting && (
          <Flex
            className={styles.clickCapture}
            onClick={(e) => { e.stopPropagation(); onToggleSelect(itemId); }}
          />
        )}
      </Flex>
    );
  };

  return (
    <Flex
      vertical
      className={root()}
    >
      {/* ── Hero banner — always rendered ─────────────────────────────── */}
      <Flex
        vertical
        className={clsx(styles.heroBanner, hasHeroColor && styles.heroBannerColored)}
        style={hasHeroColor ? { backgroundColor: heroColor } as React.CSSProperties : undefined}
      >
        {/* Layered gradient for pastel heroes — soft atmospheric fade */}
        {hasHeroColor && (
          <>
            <Flex className={styles.heroGradient} />
            <Flex className={styles.heroGlow} />
          </>
        )}

        {/* Hero content */}
        <Flex
          vertical
          justify="end"
          className={heroContent({ constrained: !wideLayout, colored: hasHeroColor })}
        >
          <Flex vertical>
            {/* Row 1: Icon + Title + Action button */}
            <Flex
              align="center"
              gap={space[3]}
              className={clsx((subtitle || heroCount) && styles.titleRowMarginBottom)}
            >
              {heroIcon && (
                <Flex
                  align="center"
                  justify="center"
                  className={heroIconWrap({ colored: hasHeroColor })}
                >
                  <Icon name={heroIcon} size={hasHeroColor ? 24 : 20} weight={iconWeight.light} className={styles.heroIcon} />
                </Flex>
              )}
              {onRenameTitle ? (
                <EditableText
                  value={title}
                  onChange={(val) => { if (val.trim() && val.trim() !== title) onRenameTitle(val.trim()); }}
                  editing={editing}
                  onEditingChange={setEditing}
                  size="lg"
                  className={styles.renameInput}
                  renderDisplay={(val) => (
                    <Flex
                      align="center"
                      gap={space[2]}
                      className={clsx(styles.titleClickable, styles.titleCursorPointer)}
                    >
                      <Text className={styles.titleText}>{val}</Text>
                      <Icon name="edit" size={14} weight={300} className={styles.editIcon} />
                    </Flex>
                  )}
                />
              ) : (
                <Text className={styles.titleText}>{title}</Text>
              )}
              {/* Header buttons: search, tag filter, options, actions — all on title row */}
              <Flex align="center" gap={space[3]} className={styles.headerActions}>
                {onSearch && (
                  <ExpandableSearch
                    value={searchQuery}
                    onChange={handleSearch}
                    onClear={() => handleSearch('')}
                    label={searchPlaceholder || 'Search...'}
                    buttonSize="lg"
                    className={styles.headerIconBtn}
                  />
                )}
                {toolbarExtras}
                {tags && tags.length > 0 && onTagsChange && (
                  <TagFilterDropdown
                    tags={tags}
                    selectedTags={selectedTags ?? []}
                    onTagsChange={onTagsChange}
                  />
                )}
                {((sortOptions && sortOptions.length > 0) || onViewChange || (typeOptions && typeOptions.length > 0)) && (
                  <Dropdown
                    trigger={['click']}
                    placement="bottom-end"
                    menu={{
                      items: [
                        ...(typeOptions && typeOptions.length > 0 && onTypeChange ? [
                          { key: 'type-header', label: 'Type', type: 'group' as const },
                          ...typeOptions.map((opt) => ({
                            key: `type-${opt.key}`,
                            label: opt.label,
                            icon: (activeType ?? 'All') === opt.key
                              ? <Icon name="check" size={14} weight={iconWeight.light} />
                              : (opt.icon ?? <Flex className={styles.sortSpacer} />),
                            onClick: () => onTypeChange(opt.key),
                          })),
                        ] : []),
                        ...((typeOptions && typeOptions.length > 0 && (sortOptions?.length || onViewChange)) ? [
                          { key: 'd-type', label: '', type: 'divider' as const },
                        ] : []),
                        ...(sortOptions && sortOptions.length > 0 && onSortChange ? [
                          { key: 'sort-header', label: 'Sort By', type: 'group' as const },
                          ...sortOptions.map((opt) => ({
                            key: `sort-${opt.key}`,
                            label: opt.label,
                            icon: activeSort === opt.key
                              ? <Icon name="check" size={14} weight={iconWeight.light} />
                              : (opt.icon ?? <Flex className={styles.sortSpacer} />),
                            onClick: () => onSortChange(opt.key),
                          })),
                        ] : []),
                        ...((sortOptions && sortOptions.length > 0 && onViewChange) ? [
                          { key: 'd-layout', label: '', type: 'divider' as const },
                        ] : []),
                        ...(onViewChange ? [
                          { key: 'layout-header', label: 'Layout', type: 'group' as const },
                          {
                            key: 'view-grid',
                            label: 'Grid View',
                            icon: <Icon name="grid_view" size={14} weight={iconWeight.light} />,
                            onClick: () => onViewChange('grid'),
                            extra: viewMode === 'grid' ? (
                              <Flex className={styles.viewDot} />
                            ) : undefined,
                          },
                          {
                            key: 'view-list',
                            label: 'List View',
                            icon: <Icon name="view_list" size={14} weight={iconWeight.light} />,
                            onClick: () => onViewChange('list'),
                            extra: viewMode === 'list' ? (
                              <Flex className={styles.viewDot} />
                            ) : undefined,
                          },
                        ] : []),
                      ],
                    }}
                  >
                    <IconButton
                      size="lg"
                      aria-label="Options"
                      className={styles.headerIconBtn}
                    >
                      <Icon name="more_horiz" size={20} weight={iconWeight.light} />
                    </IconButton>
                  </Dropdown>
                )}
                {actions}
              </Flex>
            </Flex>

            {/* Subtitle or count */}
            {subtitle && (
              <Text
                className={clsx(styles.subtitleText, heroIcon && styles.subtitleIndented)}
              >
                {subtitle}
              </Text>
            )}
            {heroCount && !subtitle && (
              <Text
                className={clsx(styles.heroCountText, heroIcon && styles.heroCountIndented)}
              >
                {heroCount}
              </Text>
            )}
          </Flex>
        </Flex>
      </Flex>

      {/* ── Content area ─────────────────────────────────────────────── */}
      <Flex
        vertical
        className={contentArea({ constrained: !wideLayout })}
      >
        {/* ── Toolbar: ownership filter ──────────────────────────────── */}
        {onAccessFilterChange && (
          <Flex className={styles.filterRow}>
            <Segmented
              options={[
                { value: 'all', label: 'All' },
                { value: 'shared', label: 'Shared with me' },
              ]}
              value={accessFilter ?? 'all'}
              onChange={v => onAccessFilterChange(v as AccessFilter)}
              ghost
            />
          </Flex>
        )}

        {/* ── Grid ───────────────────────────────────────────────────────── */}
        {items.length > 0 ? (
          <>
            {viewMode === 'list' && tableColumns ? (
              <Table
                dataSource={items as Record<string, unknown>[]}
                columns={tableColumns as Parameters<typeof Table>[0]['columns']}
                rowKey={(record) => keyExtractor(record as T)}
                pagination={false}
                size="large"
                onRow={onTableRow}
                rowSelection={tableRowSelection}
              />
            ) : viewMode === 'list' ? (
              <Flex
                vertical
                gap={space[2]}
                className={clsx(!pagination && styles.listPaddingBottom)}
              >
                {items.map((item, index) => {
                  const itemId = keyExtractor(item);
                  const isSelected = selectedIds?.has(itemId) ?? false;
                  const hasSelection = !!onToggleSelect;
                  return (
                    <Flex
                      key={itemId}
                      className={clsx(
                        styles.selectableItem,
                        hasSelection && selectedIds && selectedIds.size > 0 && !isSelected && styles.selectableItemDimmed,
                      )}
                      onClick={hasSelection ? (e) => { e.stopPropagation(); onToggleSelect(itemId); } : undefined}
                    >
                      {hasSelection && (
                        <Flex
                          onClick={(e) => e.stopPropagation()}
                          className={clsx(styles.checkboxOverlay, isSelected && styles.checkboxOverlayVisible)}
                        >
                          <Checkbox
                            checked={isSelected}
                            onChange={() => onToggleSelect(itemId)}
                            round
                          />
                        </Flex>
                      )}
                      {renderCard(item, index, 'list')}
                    </Flex>
                  );
                })}
              </Flex>
            ) : cardLayout === 'pageGrid' ? (
              <PageCardGrid
                minWidth={180}
                className={clsx(!pagination && styles.gridPaddingBottom)}
              >
                {items.map(renderSelectableGridItem)}
              </PageCardGrid>
            ) : (
              <div
                className={clsx(
                  styles.grid,
                  !pagination && styles.gridPaddingBottom,
                )}
                style={gridMinWidth ? { '--grid-min-width': `${gridMinWidth}px` } as React.CSSProperties : undefined}
              >
                {items.map(renderSelectableGridItem)}
              </div>
            )}
            {pagination}
          </>
        ) : (
          /* ── Empty state ──────────────────────────────────────────────── */
          <Flex
            vertical
            align="center"
            justify="center"
            className={styles.emptyState}
          >
            <Flex align="center" justify="center" className={styles.emptyIllustrationWrap}>
              <Flex className={styles.emptyGlow} />
              {emptyIllustration === 'documents' && <DocumentsIllustration className={styles.emptyIllustration} />}
              {emptyIllustration === 'collections' && <CollectionsIllustration className={styles.emptyIllustration} />}
              {emptyIllustration === 'trash' && <TrashIllustration className={styles.emptyIllustration} />}
            </Flex>
            {emptyTitle && (
              <Text className={styles.emptyTitle}>{emptyTitle}</Text>
            )}
            {emptyMessage && (
              <Text className={styles.emptyDescription}>{emptyMessage}</Text>
            )}
          </Flex>
        )}
      </Flex>

      {/* ── Selection floating bar ──────────────────────────────────────── */}
      {(() => {
        const tableCount = tableRowSelection?.selectedRowKeys.length ?? 0;
        const gridCount = (onToggleSelect && selectedIds) ? selectedIds.size : 0;
        const count = tableCount > 0 ? tableCount : gridCount;
        if (count === 0 || !selectionActions) return null;
        return (
          <motion.div
            initial={shouldReduce ? { opacity: 0 } : { y: space[5], opacity: 0 }}
            animate={shouldReduce ? { opacity: 1 } : { y: 0, opacity: 1 }}
            exit={shouldReduce ? { opacity: 0 } : { y: space[5], opacity: 0 }}
            className={styles.selectionBar}
          >
            <Flex
              align="center"
              gap={space[4]}
              className={styles.selectionBarInner}
            >
              <Text className={styles.selectionCount}>
                {count} selected
              </Text>
              {selectionActions}
            </Flex>
          </motion.div>
        );
      })()}
    </Flex>
  );
}
